import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import Http from "../../Http";
import { Insurance_API } from "../../Helper";
export default function CarInsuranceCalculator() {
    const [insuranceData, setInsuranceData] = useState([]);
      const [loading, setLoading] = useState(true);
      const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    carValue: 600000,
    carAge: 3,
    zone: 'A',
    engineCC: 1200,
    ncbPercent: 0
  });
  
  const [result, setResult] = useState(null);
  const [pieData, setPieData] = useState([]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData({
      ...formData,
      [id]: ['carValue', 'carAge', 'engineCC', 'ncbPercent'].includes(id) ? parseInt(value) : value
    });
  };

  const handleSliderChange = (e) => {
    const { id, value } = e.target;
    setFormData({
      ...formData,
      [id]: parseInt(value)
    });
  };

  const formatIndianCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(value);
  };

  const calculatePremium = () => {
    const { carValue, carAge, zone, engineCC, ncbPercent } = formData;
    
    // Base rate calculation
    let baseRate = 0.03; // 3% of IDV base rate
    
    // Additional rate for older cars
    if (carAge > 5) baseRate += 0.01;
    
    // Additional rate for metro zones
    if (zone === 'A') baseRate += 0.005;
    
    // Additional rate for higher engine capacity
    if (engineCC > 1500) baseRate += 0.01;
    
    // Calculate base premium
    let basePremium = carValue * baseRate;
    
    // Calculate NCB discount
    let ncbDiscount = (ncbPercent / 100) * basePremium;
    
    // Calculate premium after NCB
    let premiumAfterNCB = basePremium - ncbDiscount;
    
    // Calculate GST
    const gst = premiumAfterNCB * 0.18;
    
    // Calculate total
    const total = premiumAfterNCB + gst;
    
    // Set result
    const newResult = {
      basePremium,
      ncbDiscount,
      premiumAfterNCB,
      gst,
      total
    };
    
    setResult(newResult);
    
    // Update pie chart data
    setPieData([
      { name: 'Premium After NCB', value: premiumAfterNCB },
      { name: 'GST', value: gst }
    ]);
  };

  // Calculate on initial render
  useEffect(() => {
    calculatePremium();
  }, []);

  // Calculate whenever form data changes
  useEffect(() => {
    calculatePremium();
  }, [formData]);

  const COLORS = ['#0088FE', '#00C49F'];
  function Icon({ id, open }) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className={`${
          id === open ? "rotate-180" : ""
        } h-5 w-5 transition-transform`}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  }

  const [activeIndex, setActiveIndex] = useState(0);

  const faqs = [
    {
        question: 'Is the premium shown final?',
        answer: 'No, it’s an estimate. Actual premium may vary after document verification.',
      },
      {
        question: 'Do I need to input chassis or engine number?',
        answer: 'No, basic model details are sufficient for estimation.',
      },
      {
        question: 'Can I use the calculator for old cars?',
        answer: 'Yes, you can calculate premiums for both new and used cars.',
      },
      {
        question: 'Will it show third-party premium separately?',
        answer: 'Yes, it breaks down Own Damage and Third-party components.',
      },
      {
        question: 'Can I choose a different IDV manually?',
        answer: 'Yes, many calculators let you increase or decrease IDV.',
      },
      {
        question: 'How accurate is the estimate?',
        answer: 'Extremely close, as it uses insurer-approved rates and formulas.',
      },
      {
        question: 'Can I save the quote?',
        answer: 'Yes, most tools allow downloading or emailing the quote.',
      },
      {
        question: 'Does it factor in discounts automatically?',
        answer: 'Yes, applicable discounts like NCB or ARAI-approved devices are included.',
      },
      {
        question: 'Do I need to pay anything to use it?',
        answer: 'No, it’s a completely free tool.',
      },
      {
        question: 'Does it work for electric cars?',
        answer: 'Yes, just select EV under fuel type.',
      },
      {
        question: 'What if I miss entering claim history?',
        answer: 'Your NCB will not be applied, leading to a higher estimate.',
      },
      {
        question: 'Can I use it for commercial vehicles?',
        answer: 'Most calculators are designed for private cars. Use commercial-specific tools.',
      },
      {
        question: 'Will insurer contact me after I calculate?',
        answer: 'Only if you opt to share your contact details.',
      },
      {
        question: 'Can I change add-ons later?',
        answer: 'Yes, before purchasing, you can recalculate with different add-ons.',
      },
      {
        question: 'Is the tool mobile-friendly?',
        answer: 'Absolutely. TheFinmaster’s calculator is optimized for smartphones and tablets.',
      },
    ];
    const toggleAccordion = (index) => {
        if (activeIndex === index) {
          // If the clicked question is already expanded, collapse it
          setActiveIndex(null);
        } else {
          // If the clicked question is not expanded, expand it
          setActiveIndex(index);
        }
      };
      useEffect(() => {
        fetchInsuranceData();
      }, []);
      const fetchInsuranceData = async () => {
        try {
          const response = await Http.get(Insurance_API);
    
          // The response structure might have nested data
          // Check if response has an insurance property (like in the original fetch response)
          const data = response.insurance || response;
    
          // Filter the insurance data to show only Car category
          const carInsuranceData = Array.isArray(data)
            ? data.filter((insurance) => insurance.category_name === "Car")
            : [];
    
          setInsuranceData(carInsuranceData);
          setLoading(false);
        } catch (error) {
          setError("Failed to fetch insurance data");
          setLoading(false);
          console.error("Error fetching data:", error);
        }
      };
  return (
    <>
     <Helmet>
         <title>Car  Insurance Plan- Car Insurance Plan Calculator Online | TheFinmaster</title>
        {/* <meta  name='description' content='RD Calculator - Check Top Banks RD Interest Rates in India. And also know How to Calculate RD interest Rates, How to use &amp; Process of RD Calculator.'/>
        <meta name='keywords' content='rd calculator, rd deposit calculator, rd recurring deposit calculator, recurring deposit cal' />
        <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/rd-calculator" /> */}
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
      <ol className="list-none p-0 inline-flex">
        <li className="flex items-center">
          <Link to="/" className="text-blue-600 hover:text-blue-800 ">Home</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
        <li className="flex items-center">
          <Link
           to="/financial-tools-calculators" className="text-blue-600 hover:text-blue-800">Financial Tools Calculators</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
          <li className="flex items-center">
          <span className="text-gray-500 ">Health Calculator</span>
        </li> 
      
      </ol>
    </nav>
    <div className="bg-gray-100 min-h-screen p-4 md:p-8">
      <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="p-6 md:p-8">
          <h1 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-8">
             Car Insurance Premium Calculator
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Form Section */}
            <div className="space-y-6">
              <div>
                <label htmlFor="carValue" className="block text-gray-700 font-medium mb-2">
                  Car's Insured Declared Value (IDV) (₹)
                </label>
                <input
                  type="number"
                  id="carValue"
                  min="100000"
                  max="10000000"
                  step="10000"
                  value={formData.carValue}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg"
                  placeholder="Enter car's IDV"
                  required
                />
                <input
                  type="range"
                  id="carValue"
                  min="100000"
                  max="10000000"
                  step="10000"
                  value={formData.carValue}
                  onChange={handleSliderChange}
                  className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer mt-2"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>₹1 Lakh</span>
                  <span>₹1 Crore</span>
                </div>
              </div>

              <div>
                <label htmlFor="carAge" className="block text-gray-700 font-medium mb-2">
                  Car Age (Years)
                </label>
                <input
                  type="number"
                  id="carAge"
                  min="0"
                  max="15"
                  value={formData.carAge}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg"
                  placeholder="Enter car age"
                  required
                />
                <input
                  type="range"
                  id="carAge"
                  min="0"
                  max="15"
                  value={formData.carAge}
                  onChange={handleSliderChange}
                  className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer mt-2"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>New</span>
                  <span>15 Years</span>
                </div>
              </div>

              <div>
                <label htmlFor="zone" className="block text-gray-700 font-medium mb-2">
                  Zone of Registration
                </label>
                <select
                  id="zone"
                  value={formData.zone}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg"
                >
                  <option value="A">Zone A (Metro Cities)</option>
                  <option value="B">Zone B (Non-Metro Cities)</option>
                </select>
              </div>

              <div>
                <label htmlFor="engineCC" className="block text-gray-700 font-medium mb-2">
                  Engine Capacity (CC)
                </label>
                <input
                  type="number"
                  id="engineCC"
                  min="500"
                  max="5000"
                  value={formData.engineCC}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg"
                  placeholder="Enter engine capacity"
                  required
                />
                <input
                  type="range"
                  id="engineCC"
                  min="500"
                  max="5000"
                  value={formData.engineCC}
                  onChange={handleSliderChange}
                  className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer mt-2"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>500 CC</span>
                  <span>5000 CC</span>
                </div>
              </div>

              <div>
                <label htmlFor="ncbPercent" className="block text-gray-700 font-medium mb-2">
                  No Claim Bonus (%)
                </label>
                <input
                  type="number"
                  id="ncbPercent"
                  min="0"
                  max="50"
                  step="5"
                  value={formData.ncbPercent}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg"
                  placeholder="Enter NCB percentage"
                  required
                />
                <input
                  type="range"
                  id="ncbPercent"
                  min="0"
                  max="50"
                  step="5"
                  value={formData.ncbPercent}
                  onChange={handleSliderChange}
                  className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer mt-2"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>0%</span>
                  <span>50%</span>
                </div>
              </div>

              <button
                onClick={calculatePremium}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 px-4 rounded-lg transition duration-300"
              >
                Calculate Premium
              </button>
            </div>

            {/* Results Section */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h2 className="text-xl font-bold text-gray-800 mb-6">Results</h2>
              
              {result && (
                <div className="space-y-6">
                  <div className="bg-white p-5 rounded-lg shadow">
                    <h3 className="text-lg font-semibold text-gray-700 mb-4">Premium Details</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Car IDV:</span>
                        <span className="font-medium">{formatIndianCurrency(formData.carValue)}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Car Age:</span>
                        <span className="font-medium">{formData.carAge} years</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Zone:</span>
                        <span className="font-medium">{formData.zone === 'A' ? 'Zone A (Metro)' : 'Zone B (Non-Metro)'}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Engine Capacity:</span>
                        <span className="font-medium">{formData.engineCC} CC</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">NCB Percent:</span>
                        <span className="font-medium">{formData.ncbPercent}%</span>
                      </div>
                      
                      <div className="border-t border-gray-200 my-3"></div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Base Premium:</span>
                        <span className="font-medium">{formatIndianCurrency(result.basePremium)}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">NCB Discount:</span>
                        <span className="font-medium">- {formatIndianCurrency(result.ncbDiscount)}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Premium After NCB:</span>
                        <span className="font-medium">{formatIndianCurrency(result.premiumAfterNCB)}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">GST (18%):</span>
                        <span className="font-medium">{formatIndianCurrency(result.gst)}</span>
                      </div>
                      
                      <div className="border-t border-gray-200 my-3"></div>
                      
                      <div className="flex justify-between text-lg font-bold">
                        <span className="text-gray-800">Total Annual Premium:</span>
                        <span className="text-blue-600">{formatIndianCurrency(result.total)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white p-5 rounded-lg shadow">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Premium Breakdown</h3>
                    <div className="h-64 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => formatIndianCurrency(value)} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
      <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6">
      <div className="w-full md:w-2/3 p-4 mx-auto">
          <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            What is a Car Insurance Premium Calculator?
          </h1>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            A Car Insurance Premium Calculator is an advanced online tool designed to estimate the premium amount you would need to pay for insuring your car. It helps policyholders and potential buyers make informed decisions by providing an instant quote based on various input factors such as the car's make and model, registration year, fuel type, location, and more. Instead of going through lengthy consultations or reading complex policy brochures, you can use this intuitive tool to get a real-time, approximate figure for your insurance premium.
          </p>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            Whether you're looking to purchase a Third-party Liability Policy or a Comprehensive Car Insurance Policy, a premium calculator simplifies your buying journey. It ensures that you choose a plan that aligns with your budget and coverage requirements. It’s especially useful when comparing premiums across insurers and understanding how different parameters affect your premium amount.
          </p>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is a Car Insurance Premium?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              A car insurance premium is the amount you pay to an insurance company in exchange for coverage against damages or losses involving your car. This premium can be paid monthly, quarterly, annually, or as per the insurer's terms. The amount varies significantly based on multiple factors such as the type of policy chosen, car specifications, geographical location, add-on covers, and your claim history.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Your premium acts as a safeguard, ensuring financial protection in case of accidents, theft, natural calamities, fire, and third-party liabilities. A well-calculated premium ensures you're not overpaying while still enjoying robust protection. Premiums for new cars are generally higher than those for used cars. Similarly, premiums for comprehensive plans are higher but offer greater peace of mind than basic third-party plans.
            </p>
          </div>
          <div className="container mx-auto px-4">
          <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
          Popular Car Insurance Cards
            </h1>
  <div className="flex flex-col items-center space-y-6 mt-4">
    
  {insuranceData.slice(0, 5).map((insurance) => (
      <div
        key={insurance.id}
        className="w-full max-w-4xl bg-white rounded-2xl shadow hover:shadow-lg transition-shadow duration-300 p-4"
      >
        <div className="flex flex-col md:flex-row items-center md:items-start md:justify-between gap-6">
         
          <Link to={`/car_insurance/${insurance.page_url}`}
            className="w-full md:w-[180px] flex justify-center md:justify-start"
          >
            <img
              src={insurance.image}
              alt={insurance.page_title || "Insurance Provider"}
              className="h-18 object-contain"
            />
          </Link>

   
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center md:text-left">
            <div>
              <p className="text-gray-500 text-sm font-medium">Premium Starting</p>
              <h4 className="text-lg font-bold">
              ₹{insurance.premium_start}
                
              </h4>
            </div>
            <div>
              <p className="text-gray-500 text-sm font-medium">Claim Settlement</p>
              <h4 className="text-lg font-bold">{insurance.claim_settlement}%</h4>
            </div>
            <div>
              <p className="text-gray-500 text-sm font-medium">Network Garages</p>
              <h4 className="text-lg font-bold">{insurance.network_garages}+</h4>
            </div>
          </div>


          <div className="flex flex-col sm:flex-row items-center gap-2 mt-4 md:mt-0">
            <a
              href={insurance.bank_link}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto"
            >
              <button className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded w-full sm:w-auto">
                Get Quote!
              </button>
            </a>
                    <Link to={`/car_insurance/${insurance.page_url}`} className="w-full sm:w-auto">
              <button className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded w-full sm:w-auto">
                See Details
              </button>
            </Link>
          </div>
        </div>
      </div>
    ))}
  </div>
</div>
          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How to Use a Car Insurance Premium Calculator?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Using a car insurance premium calculator is straightforward and hassle-free. Follow these easy steps:
            </p>
            <ul className="list-decimal pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Visit the calculator tool:</strong> Access a trusted platform like TheFinmaster.</li>
              <li><strong>Enter vehicle details:</strong> Include the car brand, model, fuel type, variant, and registration year.</li>
              <li><strong>Provide location information:</strong> Input your city or PIN code to factor in regional risk.</li>
              <li><strong>Specify insurance type:</strong> Choose between Third-party, Comprehensive, or Own Damage policies.</li>
              <li><strong>Select add-ons:</strong> Opt for zero depreciation, engine protection, roadside assistance, etc., as per need.</li>
              <li><strong>Mention claim history:</strong> Input whether you've claimed insurance in the previous year to factor in NCB (No Claim Bonus).</li>
              <li><strong>Get the quote:</strong> Click 'Calculate' to receive the estimated premium instantly.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              This calculator gives you the flexibility to tweak variables and see how small changes influence the final premium, thus helping you find the optimal balance between cost and coverage.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is the Car Insurance Premium Formula?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              While each insurer may use a slightly different formula, the general structure used to calculate car insurance premiums is:
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Premium = Own Damage Premium + Third-Party Liability + Personal Accident Cover + Add-ons − Discounts/NCB
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Key components include:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Own Damage Premium:</strong> Depends on Insured Declared Value (IDV) and car specifications.</li>
              <li><strong>Third-Party Liability:</strong> Fixed as per IRDAI norms based on engine capacity.</li>
              <li><strong>Personal Accident Cover:</strong> A mandatory coverage for owner-driver, typically ₹15 lakh.</li>
              <li><strong>Add-ons:</strong> Optional features like roadside assistance, return to invoice, and more.</li>
              <li><strong>Discounts:</strong> Includes No Claim Bonus (NCB), voluntary deductible, and membership discounts.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Understanding this formula helps you decode how each component contributes to your final premium.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Real-Life Example: How the Calculator Works
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Scenario:</strong> Meera owns a 2020 Hyundai i20 Sportz petrol model in Bangalore and wants a comprehensive policy with zero depreciation and engine protection add-ons.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Steps Followed:</strong>
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Entered vehicle details: Hyundai i20, 2020, Petrol</li>
              <li>Selected city: Bangalore</li>
              <li>Opted for comprehensive plan</li>
              <li>Chose add-ons: Zero depreciation + Engine protection</li>
              <li>No previous claims, so NCB applicable</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Result:</strong> Premium estimate = ₹12,750 annually. Meera also saw similar quotes ranging between ₹11,800 and ₹13,200 and shortlisted a policy with the highest claim settlement ratio.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Information Required for the Premium Calculation
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              To ensure the most accurate estimate, a car insurance premium calculator requires the following information:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Car make and model</li>
              <li>Year of registration</li>
              <li>Variant (e.g., base, top-end)</li>
              <li>Fuel type (Petrol, Diesel, CNG, Electric)</li>
              <li>RTO location or city</li>
              <li>Type of policy (Third-party/Comprehensive)</li>
              <li>Add-ons selected</li>
              <li>Claim history (for NCB applicability)</li>
              <li>Previous insurance details (optional)</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Providing complete and truthful information ensures an accurate estimate and avoids issues at the time of claim settlement.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Key Benefits of Using a Car Insurance Premium Calculator
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              A premium calculator doesn’t just save time; it empowers you to make smarter financial decisions. Here’s how:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Instant Quotes:</strong> Get premium amounts in seconds without waiting for agent calls.</li>
              <li><strong>Real-Time Comparisons:</strong> Evaluate various plans side-by-side for better decision-making.</li>
              <li><strong>Full Transparency:</strong> Understand how each input affects the premium.</li>
              <li><strong>Cost Planning:</strong> Adjust variables to match your budget.</li>
              <li><strong>Avoid Overpaying:</strong> Customize policy features without unnecessary add-ons.</li>
              <li><strong>Accurate NCB Calculations:</strong> Automatically factor in previous claim history.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              With TheFinmaster’s premium calculator, you get reliable quotes from trusted insurers within seconds.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How is Car Insurance Premium Determined?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The premium is determined using a mix of vehicle-related, personal, and policy-specific parameters. Here’s what goes into the assessment:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>IDV (Insured Declared Value):</strong> Higher IDV increases the premium.</li>
              <li><strong>Age of the Car:</strong> Older cars usually attract lower premiums (except for add-on-heavy policies).</li>
              <li><strong>Vehicle Make and Model:</strong> High-end or performance cars have higher premiums.</li>
              <li><strong>Geographical Zone:</strong> Cities with high traffic or theft rates lead to increased premiums.</li>
              <li><strong>Engine Capacity:</strong> Higher CC cars attract higher third-party premiums.</li>
              <li><strong>Add-ons Chosen:</strong> Each add-on adds to the total cost.</li>
              <li><strong>Claim History:</strong> Clean history can fetch up to 50% NCB.</li>
              <li><strong>Security Features:</strong> Installation of anti-theft devices may reduce the premium.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Understanding these variables helps you predict and control premium costs effectively.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How to Reduce Car Insurance Premium?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              If you're looking to keep your car insurance premium within budget, here are practical steps to reduce it:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Install Security Devices:</strong> Certified anti-theft systems reduce risk and earn discounts.</li>
              <li><strong>Opt for Higher Deductibles:</strong> Agreeing to pay a portion of claims can lower premiums.</li>
              <li><strong>Utilize No Claim Bonus (NCB):</strong> Avoid claims for minor issues to retain your bonus.</li>
              <li><strong>Avoid Unnecessary Add-ons:</strong> Stick to essential add-ons only.</li>
              <li><strong>Renew On Time:</strong> Avoid lapses that may reset your NCB.</li>
              <li><strong>Choose Long-Term Policies:</strong> Multi-year policies often come with better pricing.</li>
              <li><strong>Buy Online:</strong> Online purchases eliminate agent fees and offer better deals.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Being proactive can significantly lower your insurance costs without compromising on protection.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How to Calculate Car Insurance Premium for New and Old Cars
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Calculating the insurance premium for new cars and old (used) cars involves different considerations due to factors like depreciation, Insured Declared Value (IDV), and claim history. TheFinmaster’s Car Insurance Calculator simplifies this process by adjusting parameters automatically based on the age of the car.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>For New Cars:</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              New cars have a higher market value and fewer uncertainties, so the premium is relatively straightforward.
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Select car make, model, and variant.</li>
              <li>Input the exact registration year and city.</li>
              <li>Choose Comprehensive or Third-party cover.</li>
              <li>Add Zero Depreciation, Engine Protect, or other relevant add-ons.</li>
              <li>Since there is no previous policy, the No Claim Bonus (NCB) starts at 0%.</li>
              <li>The calculator fetches the IDV as per ex-showroom value and estimates the premium accordingly.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Note:</strong> Brand-new cars may qualify for manufacturer-linked bundled policies (like 3-year third-party), which are also reflected in the calculator.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>For Old or Used Cars:</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Used cars come with reduced IDV, possible claim history, and potential wear-and-tear factors. Premiums are influenced more by age and condition.
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Input actual vehicle registration details (e.g., 2018 Hyundai i20).</li>
              <li>The calculator fetches the adjusted IDV after depreciation.</li>
              <li>Enter whether any claims were made in the previous policy.</li>
              <li>Based on the claim status, your NCB is applied (up to 50%).</li>
              <li>Choose necessary add-ons that are relevant for older cars (e.g., Engine Protect, Roadside Assistance).</li>
              <li>The calculator generates a customized premium quote.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Tip:</strong> Always check the previous insurer's renewal notice or policy document to get accurate IDV and NCB details for old cars.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Key Differences in Premium Calculation:</strong>
            </p>
            {/* <div className="overflow-x-auto mt-4">
              <table className="table-auto w-full border-collapse border border-gray-300">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="px-4 py-2 text-left">Factor</th>
                    <th className="px-4 py-2 text-left">New Car</th>
                    <th className="px-4 py-2 text-left">Old Car</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-300 text-gray-600">
                  <tr>
                    <td className="px-4 py-2">IDV</td>
                    <td className="px-4 py-2">Based on ex-showroom price</td>
                    <td className="px-4 py-2">Depreciated based on car’s age</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">NCB</td>
                    <td className="px-4 py-2">Not applicable (new car)</td>
                    <td className="px-4 py-2">Applied if no prior claims</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Add-ons</td>
                    <td className="px-4 py-2">Full range available</td>
                    <td className="px-4 py-2">Limited relevance in some cases</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Premium Amount</td>
                    <td className="px-4 py-2">Generally higher due to full value</td>
                    <td className="px-4 py-2">Usually lower due to depreciation</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Claims History</td>
                    <td className="px-4 py-2">Not applicable</td>
                    <td className="px-4 py-2">Affects NCB and insurer risk scoring</td>
                  </tr>
                </tbody>
              </table>
            </div> */}
            <div className="mt-4">
  <div className="hidden md:block overflow-x-auto">
    <table className="w-full table-auto border-collapse border border-gray-300">
      <thead className="bg-gray-800 text-white">
        <tr>
          <th className="px-4 py-2 text-left">Factor</th>
          <th className="px-4 py-2 text-left">New Car</th>
          <th className="px-4 py-2 text-left">Old Car</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-300 text-gray-600">
        <tr>
          <td className="px-4 py-2">IDV</td>
          <td className="px-4 py-2">Based on ex-showroom price</td>
          <td className="px-4 py-2">Depreciated based on car’s age</td>
        </tr>
        <tr>
          <td className="px-4 py-2">NCB</td>
          <td className="px-4 py-2">Not applicable (new car)</td>
          <td className="px-4 py-2">Applied if no prior claims</td>
        </tr>
        <tr>
          <td className="px-4 py-2">Add-ons</td>
          <td className="px-4 py-2">Full range available</td>
          <td className="px-4 py-2">Limited relevance in some cases</td>
        </tr>
        <tr>
          <td className="px-4 py-2">Premium Amount</td>
          <td className="px-4 py-2">Generally higher due to full value</td>
          <td className="px-4 py-2">Usually lower due to depreciation</td>
        </tr>
        <tr>
          <td className="px-4 py-2">Claims History</td>
          <td className="px-4 py-2">Not applicable</td>
          <td className="px-4 py-2">Affects NCB and insurer risk scoring</td>
        </tr>
      </tbody>
    </table>
  </div>

  {/* Mobile View */}
  <div className="block md:hidden space-y-4">
    {[
      {
        factor: 'IDV',
        newCar: 'Based on ex-showroom price',
        oldCar: 'Depreciated based on car’s age',
      },
      {
        factor: 'NCB',
        newCar: 'Not applicable (new car)',
        oldCar: 'Applied if no prior claims',
      },
      {
        factor: 'Add-ons',
        newCar: 'Full range available',
        oldCar: 'Limited relevance in some cases',
      },
      {
        factor: 'Premium Amount',
        newCar: 'Generally higher due to full value',
        oldCar: 'Usually lower due to depreciation',
      },
      {
        factor: 'Claims History',
        newCar: 'Not applicable',
        oldCar: 'Affects NCB and insurer risk scoring',
      },
    ].map((item, index) => (
      <div key={index} className="border border-gray-300 rounded-lg p-4">
        <div className="text-sm text-gray-500 mb-2 font-semibold">{item.factor}</div>
        <div className="text-gray-700"><strong>New Car:</strong> {item.newCar}</div>
        <div className="text-gray-700"><strong>Old Car:</strong> {item.oldCar}</div>
      </div>
    ))}
  </div>
</div>

            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Understanding this distinction ensures you make a well-informed, price-efficient choice based on your car’s age.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Tax Benefits on Car Insurance
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Unlike health insurance, car insurance doesn’t typically offer tax deductions under Section 80C or Section 80D. However, there are specific scenarios where tax benefits may apply:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Commercial Vehicle Insurance:</strong> If the car is used for business, the premium may be treated as a business expense.</li>
              <li><strong>Self-Employed Individuals:</strong> Car insurance premiums for vehicles used in professional work can be claimed as expenses.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              While direct tax benefits for private car owners are limited, understanding these exceptions helps maximize savings where possible.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Benefits of Using TheFinmaster Car Insurance Premium Calculator
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              TheFinmaster offers one of the most user-friendly, accurate, and data-backed car insurance premium calculators in India. Here’s why it stands out:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Zero Registration Hassle:</strong> Use the tool without needing to log in or sign up.</li>
              <li><strong>Instant Quotes from Multiple Insurers:</strong> Compare premiums from top insurers in real-time.</li>
              <li><strong>Regularly Updated Algorithms:</strong> Ensures rates reflect latest IRDAI guidelines.</li>
              <li><strong>Customizable Filters:</strong> Add, remove or adjust features to see premium changes.</li>
              <li><strong>Educational Insights:</strong> Clear breakdown of each premium component.</li>
              <li><strong>Safe & Secure:</strong> Data privacy assured with industry-grade encryption.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Whether you're a new buyer or renewing an existing policy, TheFinmaster helps you make the right insurance decisions.
            </p>
          </div>
        </div>
    
            <div className="w-full md:w-1/3 p-4">
              <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
                  <h2 className="text-lg font-semibold ">Popular Categories</h2>
                </div>
                <div className="p-2 ">
                  <ul>
                    <Link to="/financial-tools-calculators/swp-calculator">
                      <li className="py-2  hover:bg-slate-100 border-b border-gray-300">
                        SWP Calculator
                      </li>
                    </Link>
                    <Link to="/financial-tools-calculators/mutul-fund-calculator">
                      <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                        Mutual Fund Calculator
                      </li>
                    </Link>
                    <Link to="/financial-tools-calculators/sukanya-samriddhi-yojana-calculator">
                      <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                        Samriddhi Yojana Calculator
                      </li>
                    </Link>
                    <Link to="/financial-tools-calculators/fd-calculator">
                      <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                        FD Calculator
                      </li>
                    </Link>
                    <Link to="/financial-tools-calculators/rd-calculator">
                      <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                        RD Calculator
                      </li>
                    </Link>
                    <Link to="/financial-tools-calculators/nps-calculator">
                      <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                        NPS Calculator
                      </li>
                    </Link>
                    <Link to="/financial-tools-calculators/hra-exemption-calculator">
                      <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                        HRA Exemption Calculator
                      </li>
                    </Link>
                    <Link to="/financial-tools-calculators/retirement-planning-calculator">
                      <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                        Retirement Planning Calculator
                      </li>
                    </Link>
                    <Link to="/financial-tools-calculators/emi-calculator">
                      <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                        EMI Calculator
                      </li>
                    </Link>
                    <Link to="/financial-tools-calculators/simple-interest-calculator">
                      <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                        Simple Interest Calculator
                      </li>
                    </Link>
                    <Link to="/financial-tools-calculators/compound-interest-calculator">
                      <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                        Compound Interest Calculator
                      </li>
                    </Link>
                    <Link to="/financial-tools-calculators/nsc-calculator">
                      <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                        NSC Calculator
                      </li>
                    </Link>
                    <Link to="/financial-tools-calculators/step-up-sip-calculator">
                      <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                        Step Up Sip Calculator
                      </li>
                    </Link>
                    <Link to="/financial-tools-calculators/income-tax-calculator">
                      <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                        Income Tax Calculator
                      </li>
                    </Link>
                    <Link to="/financial-tools-calculators/home-loan-emi-calculator">
                      <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                        Home Loan Emi Calculator
                      </li>
                    </Link>
                    <Link to="/financial-tools-calculators/car-loan-emi-calculator">
                      <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                        Car Loan Emi Calculator
                      </li>
                    </Link>
                    {/* {categories.map(category => (
                   <Link key={category.id} to={`/category/${category.id}`}>
                     <li className="py-2 border-b border-gray-300">{category.name}</li>
                   </Link>
                 ))} */}
                  </ul>
                </div>
              </div>
              <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
                <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                  <h2 className="text-lg font-semibold">More Categories</h2>
                </div>
                <div className="p-2">
                  <ul>
                    <Link to="/financial-tools-calculators/gratuity-calculator">
                      <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                        Gratuity Calculator
                      </li>
                    </Link>
                    <Link to="/financial-tools-calculators/APY-calculator">
                      <li className="py-2 border- hover:bg-slate-100 border-gray-300">
                        APY Calculator
                      </li>
                    </Link>
                    <Link to="/financial-tools-calculators/cagr-calculator">
                      <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                        CAGR Calculator
                      </li>
                    </Link>
                    <Link to="/financial-tools-calculators/gst-calculator">
                      <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                        GST Calculator
                      </li>
                    </Link>
                    <Link to="/financial-tools-calculators/flat-vs-reducing-rate-calculator">
                      <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                        Flat Vs Reducing Rate Calculator
                      </li>
                    </Link>
                    <Link to="/financial-tools-calculators/equity-margin-calculator">
                      <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                        Equity Margin Calculator
                      </li>
                    </Link>
                    <Link to="/financial-tools-calculators/inflation-calculator">
                      <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                        Inflation Calculator
                      </li>
                    </Link>
                    <Link to="/financial-tools-calculators/salary-calculator">
                      <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                        Salary Calculator
                      </li>
                    </Link>
                    <Link to="/financial-tools-calculators/brokerage-calculator">
                      <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                        Brokerage Calculator
                      </li>
                    </Link>
                    <Link to="/financial-tools-calculators/tds-calculator">
                      <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                        TDS Calculator
                      </li>
                    </Link>
                    {/* {moreCategories.map(category => (
                      <Link key={category.id} to={`/category/${category.id}`}>
                        <li className="py-2 border-b border-gray-300">{category.name}</li>
                      </Link>
                    ))} */}
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
            <h1 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">
              Frequently Asked Questions
            </h1>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="border rounded-lg overflow-hidden shadow-md"
                >
                  <button
                    onClick={() => toggleAccordion(index)}
                    className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                  >
                    {faq.question}
                    <Icon id={index} open={activeIndex} />
                  </button>
                  {activeIndex === index && (
                    <div className="p-4 bg-gray-100">
                      <p className="text-gray-700">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
    </>
  );
}